import React, { useState } from 'react';
import { BellIcon, EyeIcon, LockIcon, SendIcon, XIcon } from 'lucide-react';
import { Avatar } from './Avatar';
import { TagPicker } from './TagPicker';
import { BORROWER_NAME } from '../../data/conditions';
import { getMember } from '../../utils/conditions';
interface CommentComposerProps {
  onSubmit: (body: string, mentions: string[], toBorrower: boolean) => void;
  borrowerSendConfirmed: boolean;
  onBorrowerSendConfirmed: () => void;
}
type Mode = 'internal' | 'borrower';
export function CommentComposer({
  onSubmit,
  borrowerSendConfirmed,
  onBorrowerSendConfirmed
}: CommentComposerProps) {
  const [mode, setMode] = useState<Mode>('internal');
  const [draft, setDraft] = useState('');
  const [tagged, setTagged] = useState<string[]>([]);
  const [confirming, setConfirming] = useState(false);
  const [notice, setNotice] = useState<{
    tagged: string[];
    borrower: boolean;
  } | null>(null);
  const isBorrower = mode === 'borrower';
  function toggleTag(id: string) {
    setTagged(prev => prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]);
  }
  function send() {
    onSubmit(draft.trim(), tagged, isBorrower);
    setNotice({
      tagged,
      borrower: isBorrower
    });
    setDraft('');
    setTagged([]);
    setMode('internal');
    setConfirming(false);
  }
  function handleSubmit(e: React.SyntheticEvent) {
    e.preventDefault();
    if (!draft.trim()) return;
    if (isBorrower && !borrowerSendConfirmed) {
      setConfirming(true);
      return;
    }
    send();
  }
  return <form onSubmit={handleSubmit} className={`border-t px-5 py-4 transition-colors ${isBorrower ? 'border-amber-200 bg-amber-50/70' : 'border-slate-200 bg-white'}`} data-id="element-75" data-mp="2o1bci97jhhcz">
      {notice && (notice.tagged.length > 0 || notice.borrower) && <div className="mb-3 space-y-1 rounded-md bg-brand/[0.07] px-3 py-2 text-xs font-medium text-brand-dark" data-id="element-76" data-mp="yoq1tp9grgzi">
          {notice.borrower && <p className="flex items-center gap-1.5" data-id="element-77" data-mp="13p5d8ba6qeeu">
              <EyeIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-78" data-mp="vljzepl1xk0b" />
              Sent to {BORROWER_NAME} via portal + email
            </p>}
          {notice.tagged.length > 0 && <p className="flex items-center gap-1.5" data-id="element-79" data-mp="15ksx9tj1firy">
              <BellIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-80" data-mp="15i6fl12fnmw0" />
              Notified {notice.tagged.map(id => getMember(id).name).join(', ')}
            </p>}
        </div>}

      <div role="radiogroup" aria-label="Who will see this" className="mb-2 inline-flex rounded-lg bg-slate-100 p-0.5 text-xs font-medium" data-id="element-81" data-mp="1ej4onxrqaklg">
        <button type="button" role="radio" aria-checked={!isBorrower} onClick={() => {
        setMode('internal');
        setConfirming(false);
      }} className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand ${!isBorrower ? 'bg-white text-charcoal shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-82" data-mp="1obacqwev0hvd">
          <LockIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-83" data-mp="2gy0jcvfyyypo" />
          Internal note
        </button>
        <button type="button" role="radio" aria-checked={isBorrower} onClick={() => setMode('borrower')} className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand ${isBorrower ? 'bg-white text-amber-800 shadow-sm ring-1 ring-amber-200' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-84" data-mp="22yir263gt3bk">
          <EyeIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-85" data-mp="1k5ev2e2ryeds" />
          Message to borrower
        </button>
      </div>

      <label htmlFor="comment-input" className="sr-only" data-id="element-86" data-mp="1a9bxzmms55xp">
        {isBorrower ? 'Message to borrower' : 'Internal note'}
      </label>
      <textarea id="comment-input" rows={3} value={draft} onChange={e => setDraft(e.target.value)} onKeyDown={e => {
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleSubmit(e);
    }} placeholder={isBorrower ? `Write a message to ${BORROWER_NAME}…` : 'Add an internal note for your team…'} className={`w-full resize-none rounded-md border bg-white px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 ${isBorrower ? 'border-amber-300 focus:border-amber-500 focus:ring-amber-500' : 'border-slate-300 focus:border-brand focus:ring-brand'}`} data-id="element-87" data-mp="1f6tvmgvcjyqe" />
      <p className={`mt-1 flex items-center gap-1 text-xs ${isBorrower ? 'text-amber-800' : 'text-slate-500'}`} data-id="element-88" data-mp="1puwzoktcsxqm">
        {isBorrower ? <>
            <EyeIcon className="h-3 w-3" aria-hidden="true" data-id="element-89" data-mp="3k8xyx6stfmr8" />
            {BORROWER_NAME} will see this in their portal and by email
          </> : <>
            <LockIcon className="h-3 w-3" aria-hidden="true" data-id="element-90" data-mp="3f7h1xmdgckfx" />
            Only your team can see this
          </>}
      </p>

      {tagged.length > 0 && <ul className="mt-2 flex flex-wrap gap-1.5" aria-label="Tagged teammates" data-id="element-91" data-mp="3rqclfg6hpj9k">
          {tagged.map(id => {
        const m = getMember(id);
        return <li key={id} className="inline-flex items-center gap-1.5 rounded-full bg-brand/10 py-0.5 pl-0.5 pr-1.5 text-xs font-medium text-brand-dark" data-id="element-92" data-mp="5bb2lzkp8jk3">
                <Avatar member={m} size="sm" data-id="element-93" data-mp="38u4d23i6ksa1" />
                {m.name}
                <button type="button" onClick={() => toggleTag(id)} aria-label={`Remove ${m.name}`} className="rounded-full p-0.5 hover:bg-brand/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand" data-id="element-94" data-mp="3dseciqaxuf1g">
                  <XIcon className="h-3 w-3" aria-hidden="true" data-id="element-95" data-mp="x2zbgc49qny6" />
                </button>
              </li>;
      })}
        </ul>}

      {confirming ? <div role="alertdialog" aria-labelledby="confirm-borrower-title" className="mt-3 rounded-md border border-amber-300 bg-white p-3" data-id="element-96" data-mp="2p52399tjuq5p">
          <p id="confirm-borrower-title" className="text-sm font-semibold text-slate-900" data-id="element-97" data-mp="333s0qqka6rul">
            Send this message to {BORROWER_NAME}?
          </p>
          <p className="mt-0.5 text-xs text-slate-600" data-id="element-98" data-mp="2ag5kahpyfsqb">
            It will appear in their borrower portal and be emailed to them. You won’t be asked again this session.
          </p>
          <div className="mt-3 flex justify-end gap-2" data-id="element-99" data-mp="26chea3gz1576">
            <button type="button" onClick={() => setConfirming(false)} className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand" data-id="element-100" data-mp="2k70i8jckcgrl">
              Keep editing
            </button>
            <button type="button" autoFocus onClick={() => {
          onBorrowerSendConfirmed();
          send();
        }} className="inline-flex items-center gap-1.5 rounded-md bg-amber-700 px-3 py-1.5 text-sm font-semibold text-white hover:bg-amber-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-600 focus-visible:ring-offset-2" data-id="element-101" data-mp="28ka3zcwvmno">
              <SendIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-102" data-mp="lxup1b4u23kq" />
              Yes, send
            </button>
          </div>
        </div> : <div className="mt-3 flex items-center justify-between gap-3" data-id="element-103" data-mp="byc68y0nsnu9">
          <TagPicker tagged={tagged} onToggle={toggleTag} data-id="element-104" data-mp="c0g87aezzcrw" />
          {isBorrower ? <button type="submit" disabled={!draft.trim()} className="inline-flex items-center gap-1.5 rounded-md bg-amber-700 px-3.5 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-amber-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-600 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300" data-id="element-105" data-mp="gvvmg3gx3aou">
              <SendIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-106" data-mp="3oe8o4jfttfv8" />
              Send to borrower
            </button> : <button type="submit" disabled={!draft.trim()} className="rounded-md bg-brand px-3.5 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-brand-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300" data-id="element-107" data-mp="3eehrab3smorz">
              Add note
            </button>}
        </div>}
    </form>;
}